import { createSlice } from '@reduxjs/toolkit';
import { getUserSettings, send2faCode, toggleTwoFA, updateSocialMedia, toggleAccountType, toggleVisibility, toggleOnlineStatusVisibility, getTwoFADetails, setupAuthenticator, verifyAuthenticator, disableAuthenticator } from './settingsAction';

const initialState = {
    updatingLink: false,
    sendingOtp: false,
    settings: null, // Stores all settings as a single object
    status: 'idle', // 'idle' | 'loading' | 'succeeded' | 'failed'
    error: null,
    settingUpAuthenticator: false,
    verifyingAuthenticator: false,
    disablingAuthenticator: false,
    authenticatorSetup: null, // { qrCode, secret, backupCodes }
};

const settingsSlice = createSlice({
    name: 'settings',
    initialState,
    reducers: {},
    extraReducers: (builder) => {
        builder
            // Get User Settings
            .addCase(getUserSettings.pending, (state) => {
                state.status = 'loading';
                state.error = null;
            })
            .addCase(getUserSettings.fulfilled, (state, action) => {
                state.status = 'succeeded';
                state.settings = action.payload;
            })
            .addCase(getUserSettings.rejected, (state, action) => {
                state.status = 'failed';
                state.error = action.payload;
            })

            // Send 2FA Code
            .addCase(send2faCode.pending, (state) => {
                state.sendingOtp = true
            })
            .addCase(send2faCode.fulfilled, (state) => {
                state.sendingOtp = false
            })
            .addCase(send2faCode.rejected, (state, action) => {
                state.sendingOtp = false
                state.error = action.payload;
            })

            // Toggle Two-Factor Authentication
            .addCase(toggleTwoFA.fulfilled, (state, action) => {
                state.status = 'succeeded';
                if (state.settings) {
                    state.settings.twoFA = action.payload.twoFA; // Update twoFA in settings
                    state.settings.twoFAMethod = action.payload.twoFAMethod;
                }
                if (!action.payload.twoFA) {
                    state.authenticatorSetup = null;
                }
            })

            // Fetch active 2FA method details
            .addCase(getTwoFADetails.fulfilled, (state, action) => {
                if (state.settings) {
                    state.settings.twoFA = action.payload.twoFA;
                    state.settings.twoFAMethod = action.payload.twoFAMethod;
                }
            })

            // Authenticator setup (QR + secret + backup codes)
            .addCase(setupAuthenticator.pending, (state) => {
                state.settingUpAuthenticator = true;
            })
            .addCase(setupAuthenticator.fulfilled, (state, action) => {
                state.settingUpAuthenticator = false;
                state.authenticatorSetup = {
                    qrCode: action.payload.qrCode,
                    secret: action.payload.secret,
                    backupCodes: action.payload.backupCodes,
                };
            })
            .addCase(setupAuthenticator.rejected, (state, action) => {
                state.settingUpAuthenticator = false;
                state.error = action.payload;
            })

            // Authenticator verify
            .addCase(verifyAuthenticator.pending, (state) => {
                state.verifyingAuthenticator = true;
            })
            .addCase(verifyAuthenticator.fulfilled, (state, action) => {
                state.verifyingAuthenticator = false;
                if (action.payload.backupCodes && state.authenticatorSetup) {
                    state.authenticatorSetup.backupCodes = action.payload.backupCodes;
                }
            })
            .addCase(verifyAuthenticator.rejected, (state, action) => {
                state.verifyingAuthenticator = false;
                state.error = action.payload;
            })

            // Authenticator disable
            .addCase(disableAuthenticator.pending, (state) => {
                state.disablingAuthenticator = true;
            })
            .addCase(disableAuthenticator.fulfilled, (state) => {
                state.disablingAuthenticator = false;
                state.authenticatorSetup = null;
                if (state.settings) {
                    state.settings.twoFA = false;
                    state.settings.twoFAMethod = null;
                }
            })
            .addCase(disableAuthenticator.rejected, (state, action) => {
                state.disablingAuthenticator = false;
                state.error = action.payload;
            })

        builder
            .addCase(updateSocialMedia.pending, (state) => {
                state.updatingLink = true;
            })
            .addCase(updateSocialMedia.fulfilled, (state, action) => {
                state.updatingLink = false
                if (state.settings) {
                    state.settings.socialData = action.payload; // Update socialData in settings
                }
            })
            .addCase(updateSocialMedia.rejected, (state, action) => {
                state.updatingLink = false
                state.error = action.payload;
            })

            // Toggle Account Type

            .addCase(toggleAccountType.fulfilled, (state, action) => {
                if (state.settings) {
                    state.settings.accountType = action.payload; // Update accountType in settings
                }
            })

            .addCase(toggleVisibility.fulfilled, (state, action) => {
                if (state.settings) {
                    state.settings.visibilityStatus = action.payload; // Update visibilityStatus in settings
                }
            })

            .addCase(toggleOnlineStatusVisibility.fulfilled, (state, action) => {
                if (state.settings) {
                    state.settings.onlineVisibility = action.payload; // Update onlineVisibility in settings
                }
            })


    },
});

export default settingsSlice.reducer;